import React, { useState, useContext, useEffect } from "react";
import {
  Banner,
  Button,
  FormLayout,
  Link,
  RadioButton,
  Select,
  SkeletonBodyText,
  InlineStack,
  BlockStack,
} from "@shopify/polaris";
import { DeleteIcon } from "@shopify/polaris-icons";

import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";
import { withErrorBoundary } from "lib/hoc";
import { FulfillmentContext } from "app/setup/modules/operator/features/_fulfillment/context";
import { GET_VENDOR_AND_PRODUCT_TYPES, GET_ADDRESS } from "app/setup/apollo/queries";
import { FulfillmentWrapper, ConsignmentShipWrapper } from "asset/styles/globalStyle";

const ConsignmentShip = (props) => {
  const {
    clickCollect,
    clickReserve,
    timeService,
    cms,
    consignmentPickup,
    consignmentShip,
    dropship,
    setClickCollect,
    setClickReserve,
    setTimeService,
    setConsignmentPickup,
    setConsignmentShip,
    setDropship,
    setSubmitEnable,
    selectedTab,
    type,
  } = useContext(FulfillmentContext);

  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const { gql } = constant;
  const [allTypes, setAllTypes] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [allAddress, setAllAddress] = useState([]);
  const [shipData, setShipData] = useState();
  const [selectRadioButton, setSelectedRadioButton] = useState();
  const clickCollectVendorIds = [];
  const dropshipVendorIds = [];
  const clickCollectProductTypeIds = [];
  const dropshipProductTypeIds = [];
  const {
    loading: vendorAndProductTypesLoading,
    error: vendorAndProductTypesError,
    data: vendorAndProductTypesData,
  } = useQuery(GET_VENDOR_AND_PRODUCT_TYPES);
  const { loading: addressLoading, error: addressError, data: addressData } = useQuery(GET_ADDRESS);
  const { method } = props;
  const setData =
    (method === constant.HYBRID && setDropship) ||
    (method === constant.HYBRID_CONSIGNMENT_PICKUP && setConsignmentPickup) ||
    (method === constant.HYBRID_CLICK_COLLECT && setClickCollect) ||
    (method === constant.HYBRID_CLICK_RESERVE && setClickReserve) ||
    (method === constant.HYBRID_TIME_SERVICE && setTimeService) ||
    setConsignmentShip;

  useEffect(() => {
    if (method === constant.HYBRID) {
      setShipData(dropship);
    }
    if (method === constant.HYBRID_CONSIGNMENT_PICKUP) {
      setShipData(consignmentPickup);
    }
    if (method === constant.HYBRID_CLICK_COLLECT) {
      setShipData(clickCollect);
    }
    if (method === constant.HYBRID_CLICK_RESERVE) {
      setShipData(clickReserve);
    }
    if (method === constant.HYBRID_TIME_SERVICE) {
      setShipData(timeService);
    }
    if (
      ![
        constant.HYBRID,
        constant.HYBRID_CONSIGNMENT_PICKUP,
        constant.HYBRID_CLICK_RESERVE,
        constant.HYBRID_CLICK_COLLECT,
        constant.HYBRID_TIME_SERVICE,
      ].includes(method)
    ) {
      setShipData(consignmentShip);
    }
  }, [method, consignmentPickup, consignmentShip, clickCollect, clickReserve, timeService, dropship]);

  const handleSelectChange = (_checked, newValue) => {
    const newSetting = {};
    if (
      [
        constant.HYBRID,
        constant.HYBRID_CONSIGNMENT_PICKUP,
        constant.HYBRID_CLICK_RESERVE,
        constant.HYBRID_CLICK_COLLECT,
        constant.HYBRID_TIME_SERVICE,
      ].includes(method)
    ) {
      newSetting.productType = "";
      newSetting.vendorId = "";
    }
    if (
      ![
        constant.HYBRID,
        constant.HYBRID_CONSIGNMENT_PICKUP,
        constant.HYBRID_CLICK_RESERVE,
        constant.HYBRID_CLICK_COLLECT,
        constant.HYBRID_TIME_SERVICE,
      ].includes(method)
    ) {
      newSetting.vendorId = "";
      newSetting.productType = "";
      newSetting.addressId = "";
    }
    setData((prevState) => ({
      ...prevState,
      by: newValue,
      setting: [newSetting],
    }));
    setSubmitEnable(true);
    setSelectedRadioButton(newValue);
  };

  useEffect(() => {
    if (vendorAndProductTypesError) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(vendorAndProductTypesError) });
      return;
    }

    const responseData = baseHelper.getResponseData(vendorAndProductTypesData, gql.GET_VENDOR_PRODUCT) || {};
    const { types = [], supplierRows = [] } = responseData;

    // Build *fresh* helper arrays each run (no external mutation)
    const selectedTypes = [];
    const dropshipProductTypeIds = [];
    const clickCollectProductTypeIds = [];

    dropship?.setting?.forEach(({ productType }) => {
      if (productType) {
        selectedTypes.push(productType);
        dropshipProductTypeIds.push(productType);
      }
    });
    consignmentPickup?.setting?.forEach(({ productType }) => {
      if (productType) selectedTypes.push(productType);
    });
    consignmentShip?.setting?.forEach(({ productType }) => {
      if (productType) selectedTypes.push(productType);
    });
    clickReserve?.setting?.forEach(({ productType }) => {
      if (productType) selectedTypes.push(productType);
    });
    clickCollect?.setting?.forEach(({ productType }) => {
      if (productType) {
        selectedTypes.push(productType);
        clickCollectProductTypeIds.push(productType);
      }
    });
    timeService?.setting?.forEach(({ productType }) => {
      if (productType) selectedTypes.push(productType);
    });

    // Create *new* objects for types (no in-place assignment)
    const formattedTypes = (types || [])
      .filter((t) => t?.value) // keep only valid
      .map((t) => {
        const { value } = t;
        let disabled = false;

        if (shipData && shipData.by !== constant.VENDOR_PRODUCT_TYPE) {
          if (selectedTypes.includes(value)) {
            // Apply your enable/disable rules
            if (
              clickCollectProductTypeIds.length &&
              clickCollectProductTypeIds.includes(value) &&
              !dropshipProductTypeIds.includes(value) &&
              type === constant.HYBRID &&
              selectedTab === 1 &&
              dropship
            ) {
              disabled = false;
            } else if (
              dropshipProductTypeIds.length &&
              dropshipProductTypeIds.includes(value) &&
              !clickCollectProductTypeIds.includes(value) &&
              type === constant.HYBRID &&
              selectedTab === 4 &&
              clickCollect
            ) {
              disabled = false;
            } else {
              disabled = true;
            }
          }
        }

        return { label: value, value, disabled };
      });

    // Vendor IDs helpers
    const selectedSuppliers = [];
    const dropshipVendorIds = [];
    const clickCollectVendorIds = [];

    dropship?.setting?.forEach(({ vendorId }) => {
      if (vendorId) {
        selectedSuppliers.push(vendorId);
        dropshipVendorIds.push(vendorId);
      }
    });
    consignmentPickup?.setting?.forEach(({ vendorId }) => {
      if (vendorId) selectedSuppliers.push(vendorId);
    });
    consignmentShip?.setting?.forEach(({ vendorId }) => {
      if (vendorId) selectedSuppliers.push(vendorId);
    });
    clickReserve?.setting?.forEach(({ vendorId }) => {
      if (vendorId) selectedSuppliers.push(vendorId);
    });
    clickCollect?.setting?.forEach(({ vendorId }) => {
      if (vendorId) {
        selectedSuppliers.push(vendorId);
        clickCollectVendorIds.push(vendorId);
      }
    });
    timeService?.setting?.forEach(({ vendorId }) => {
      if (vendorId) selectedSuppliers.push(vendorId);
    });

    // Create *new* objects for suppliers (no in-place assignment)
    const formattedSuppliers = (supplierRows || [])
      .filter((s) => s?.brandName)
      .map((s) => {
        const id = s._id;
        let disabled = false;

        if (selectedSuppliers.includes(id)) {
          if (
            clickCollectVendorIds.length &&
            clickCollectVendorIds.includes(id) &&
            !dropshipVendorIds.includes(id) &&
            type === constant.HYBRID &&
            selectedTab === 1 &&
            dropship
          ) {
            disabled = false;
          } else if (
            dropshipVendorIds.length &&
            dropshipVendorIds.includes(id) &&
            !clickCollectVendorIds.includes(id) &&
            type === constant.HYBRID &&
            selectedTab === 4 &&
            clickCollect
          ) {
            disabled = false;
          } else {
            disabled = true;
          }
        }

        return { label: s.brandName, value: id, disabled };
      });

    // Messaging (avoid checking "truthiness" of arrays themselves)
    if (responseData) {
      if (
        (selectRadioButton === constant.VENDOR_PRODUCT_TYPE && !(types.length && supplierRows.length)) ||
        (selectRadioButton === constant.PRODUCT_TYPE && !types.length) ||
        (selectRadioButton === constant.VENDOR && !supplierRows.length)
      ) {
        setBanner({
          isOpen: true,
          title: cms("message.error.consignmentShipUnavailableReason"),
          status: constant.CRITICAL,
        });
      }
      if (!types.length) {
        setBanner({ isOpen: true, title: cms("message.error.productReason"), status: constant.WARNING });
      }
      if (!supplierRows.length) {
        setBanner({ isOpen: true, title: cms("message.error.vendorReason"), status: constant.WARNING });
      }
    }

    setAllTypes(formattedTypes);
    setAllSuppliers(formattedSuppliers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vendorAndProductTypesData,
    vendorAndProductTypesError,
    dropship,
    consignmentPickup,
    clickReserve,
    clickCollect,
    timeService,
    consignmentShip,
    shipData,
    type,
    selectedTab,
    selectRadioButton,
  ]);

  useEffect(() => {
    if (addressData) {
      const responseData = baseHelper.getResponseData(addressData, gql.GET_ADDRESS);
      const responseError = baseHelper.getResponseError(addressData, gql.GET_ADDRESS);
      if (responseData && responseData.addressList) {
        const { addressList } = responseData;
        const formattedAddress = addressList
          .filter((item) => item.label && item)
          .map(({ _id: addressId, label }) => ({
            label,
            value: addressId,
          }));
        setAllAddress(formattedAddress);
      }
      if (responseError) {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
      }
    }

    if (addressError) {
      setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(addressError) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressData, addressError]);

  const removeItem = (id) => {
    if (shipData && shipData.by === constant.VENDOR) {
      const vendor = [...shipData.setting];
      vendor.splice(id, 1);
      // vendor.pop();
      setData((prevState) => ({
        ...prevState,
        setting: vendor,
      }));
    }

    if (shipData && shipData.by === constant.PRODUCT_TYPE) {
      const product = [...shipData.setting];
      product.splice(id, 1);
      // product.pop();
      setData((prevState) => ({
        ...prevState,
        setting: product,
      }));
    }

    if (shipData && shipData.by === constant.VENDOR_PRODUCT_TYPE) {
      const both = [...shipData.setting];
      both.splice(id, 1);
      // both.pop();
      setData((prevState) => ({
        ...prevState,
        setting: both,
      }));
    }
    setSubmitEnable(true);
  };

  const updateValue = (option, value, item) => {
    let duplicate = false;
    const updatedAdvancedValue = [...shipData.setting];
    if (shipData && shipData.by === constant.PRODUCT_TYPE) {
      updatedAdvancedValue.forEach((data) => {
        if (data.productType === value) {
          updatedAdvancedValue[item].productType = null;
          updatedAdvancedValue[item].addressId = null;
          setBanner({
            isOpen: true,
            title: cms("message.error.alreadyExists", { item: cms("label.provider") }),
            status: constant.CRITICAL,
          });
          duplicate = true;
        }
        setSubmitEnable(true);
      });
    }

    if ((shipData && shipData.by === constant.VENDOR) || (shipData && shipData.by === constant.VENDOR_PRODUCT_TYPE)) {
      updatedAdvancedValue.forEach((data) => {
        if (data.vendorId === value) {
          updatedAdvancedValue[item].vendorId = null;
          updatedAdvancedValue[item].addressId = null;
          setBanner({
            isOpen: true,
            title: cms("message.error.alreadyExists", { item: cms("label.product") }),
            status: constant.CRITICAL,
          });
          duplicate = true;
        }
        setSubmitEnable(true);
      });
    }

    if (duplicate) {
      return;
    }
    if (option === constant.VENDOR_ID) {
      const clonedItem = { ...updatedAdvancedValue[item] };
      clonedItem.vendorId = value;

      updatedAdvancedValue[item] = clonedItem;
    }

    if (option === constant.PRODUCT_TYPE) {
      updatedAdvancedValue[item].productType = value;
    }

    if (option === constant.ADDRESS_ID) {
      updatedAdvancedValue[item].addressId = value;
    }

    setData((prevState) => ({
      ...prevState,
      setting: updatedAdvancedValue,
    }));
    setSubmitEnable(true);
  };

  const consignmentByVendor = (vendorData, id) => {
    if (shipData && shipData.by === constant.VENDOR) {
      return (
        <BlockStack>
          <FormLayout>
            <InlineStack gap="400" blockAlign="center">
              {([
                constant.HYBRID,
                constant.HYBRID_CONSIGNMENT_PICKUP,
                constant.HYBRID_CLICK_RESERVE,
                constant.HYBRID_CLICK_COLLECT,
                constant.HYBRID_TIME_SERVICE,
              ].includes(method) && (
                <Select
                  placeholder={cms("placeholder.vendors")}
                  options={allSuppliers}
                  value={vendorData.vendorId}
                  onChange={(value) => updateValue(constant.VENDOR_ID, value, id)}
                />
              )) || (
                <InlineStack gap="400">
                  <Select
                    placeholder={cms("placeholder.vendors")}
                    options={allSuppliers}
                    value={vendorData.vendorId}
                    onChange={(value) => updateValue(constant.VENDOR_ID, value, id)}
                  />
                  <Select
                    placeholder={cms("placeholder.addressLabel")}
                    options={allAddress}
                    value={vendorData.addressId}
                    onChange={(value) => updateValue(constant.ADDRESS_ID, value, id)}
                  />
                </InlineStack>
              )}
              <InlineStack>
                <div className="delete-icon">
                  <Button variant="plain" onClick={() => removeItem(id)}>
                    <i className="far fa-trash fa-lg" style={{ color: "red" }} />
                  </Button>
                </div>
              </InlineStack>
            </InlineStack>
          </FormLayout>
          <br />
        </BlockStack>
      );
    }
    return null;
  };

  const consignmentByProductType = (productData, id) => {
    if (shipData && shipData.by === constant.PRODUCT_TYPE) {
      return (
        <InlineStack blockAlign="center" gap="400" wrap={false}>
          {/* Left side: selects (grow) */}
          <InlineStack gap="400" wrap>
            {[
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method) ? (
              <div style={{ flex: 1, minWidth: 240 }}>
                <Select
                  placeholder={cms("placeholder.productType")}
                  options={allTypes}
                  onChange={(value) => updateValue(constant.PRODUCT_TYPE, value, id)}
                  value={productData.productType}
                />
              </div>
            ) : (
              <>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <Select
                    options={allTypes}
                    placeholder={cms("placeholder.productType")}
                    onChange={(value) => updateValue(constant.PRODUCT_TYPE, value, id)}
                    value={productData.productType}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <Select
                    placeholder={cms("placeholder.addressLabel")}
                    options={allAddress}
                    value={productData.addressId}
                    onChange={(value) => updateValue(constant.ADDRESS_ID, value, id)}
                  />
                </div>
              </>
            )}
          </InlineStack>

          {/* Right side: delete button */}
          <Button variant="plain" tone="critical" onClick={() => removeItem(id)} accessibilityLabel="Remove row">
            <i className="far fa-trash fa-lg" style={{ color: "red" }} />
          </Button>
        </InlineStack>
      );
    }
    return null;
  };

  const consignmentByBoth = (data, id) => {
    if (shipData && shipData.by === constant.VENDOR_PRODUCT_TYPE) {
      return (
        <BlockStack gap="100">
          <ConsignmentShipWrapper>
            {([
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method) && (
              <InlineStack wrap={false} gap="200" blockAlign="center">
                <Select
                  placeholder={cms("placeholder.vendors")}
                  options={allSuppliers}
                  value={data.vendorId}
                  onChange={(value) => updateValue(constant.VENDOR_ID, value, id)}
                />
                <Select
                  options={allTypes}
                  placeholder={cms("placeholder.productType")}
                  onChange={(value) => updateValue(constant.PRODUCT_TYPE, value, id)}
                  value={data.productType}
                />
                <div className="delete-icon">
                  <Button variant="plain" onClick={() => removeItem(id)}>
                    <i className="far fa-trash fa-lg" style={{ color: "red" }} />
                  </Button>
                </div>
              </InlineStack>
            )) || (
              <InlineStack wrap={false} gap="200" blockAlign="center">
                <Select
                  options={allSuppliers}
                  placeholder={cms("placeholder.vendors")}
                  value={data.vendorId}
                  onChange={(value) => updateValue(constant.VENDOR_ID, value, id)}
                />
                <Select
                  placeholder={cms("placeholder.productType")}
                  options={allTypes}
                  onChange={(value) => updateValue(constant.PRODUCT_TYPE, value, id)}
                  value={data.productType}
                />
                <Select
                  placeholder={cms("placeholder.addressLabel")}
                  options={allAddress}
                  value={data.addressId}
                  onChange={(value) => updateValue(constant.ADDRESS_ID, value, id)}
                />
                <div className="delete-icon">
                  <Button variant="plain" onClick={() => removeItem(id)}>
                    <i className="far fa-trash fa-lg" style={{ color: "red" }} />
                  </Button>
                </div>
              </InlineStack>
            )}
          </ConsignmentShipWrapper>
        </BlockStack>
      );
    }
    return null;
  };

  const addItem = () => {
    switch (shipData && shipData.by) {
      case constant.VENDOR:
        {
          const vendorItem = (shipData && shipData.setting && [...shipData.setting]) || [];
          if (vendorItem.length > 0) {
            const line = vendorItem[vendorItem.length - 1];
            let keys = Object.keys(line);
            if (shipData.by === constant.VENDOR) {
              keys = keys.filter((newKey) => newKey !== constant.PRODUCT_TYPE);
            }
            let isInvalid = false;
            keys.forEach((key) => {
              if (!line[key]) {
                isInvalid = true;
              }
            });
            if (isInvalid) {
              setBanner({
                isOpen: true,
                title: cms("message.requireFields"),
                status: constant.CRITICAL,
              });
              return;
            }
          }
          if (
            ![
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            vendorItem.push({
              vendorId: "",
              addressId: "",
            });
          }

          if (
            [
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            vendorItem.push({
              vendorId: "",
            });
            // if (method === constant.HYBRID) {
            //   setConsignmentPickup({});
            // }
            // if (method === constant.HYBRID_CONSIGNMENT_PICKUP) {
            //   setDropship({});
            // }
            // setConsignmentShip({});
          }
          setData((prevState) => ({
            ...prevState,
            setting: vendorItem,
          }));
        }
        break;
      case constant.PRODUCT_TYPE:
        {
          const productItem = (shipData && shipData.setting && [...shipData.setting]) || [];
          if (productItem.length > 0) {
            const line = productItem[productItem.length - 1];
            let keys = Object.keys(line);
            if (shipData.by === constant.PRODUCT_TYPE) {
              keys = keys.filter((newKey) => newKey !== constant.VENDOR_ID);
            }
            let isInvalid = false;
            keys.forEach((key) => {
              if (!line[key]) {
                isInvalid = true;
              }
            });
            if (isInvalid) {
              setBanner({
                isOpen: true,
                title: cms("message.requireFields"),
                status: constant.CRITICAL,
              });
              return;
            }
          }

          if (
            ![
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            productItem.push({
              productType: "",
              addressId: "",
            });
          }

          if (
            [
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            productItem.push({
              productType: "",
            });
          }
          setData((prevState) => ({
            ...prevState,
            setting: productItem,
          }));
        }
        break;
      case constant.VENDOR_PRODUCT_TYPE:
        {
          const productItem = (shipData && shipData.setting && [...shipData.setting]) || [];
          if (productItem.length > 0) {
            const line = productItem[productItem.length - 1];
            const keys = Object.keys(line);
            let isInvalid = false;
            keys.forEach((key) => {
              if (!line[key]) {
                isInvalid = true;
              }
            });
            if (isInvalid) {
              setBanner({
                isOpen: true,
                title: cms("message.requireFields"),
                status: constant.CRITICAL,
              });
              return;
            }
          }

          if (
            ![
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            productItem.push({
              vendorId: "",
              productType: "",
              addressId: "",
            });
          }

          if (
            [
              constant.HYBRID,
              constant.HYBRID_CONSIGNMENT_PICKUP,
              constant.HYBRID_CLICK_RESERVE,
              constant.HYBRID_CLICK_COLLECT,
              constant.HYBRID_TIME_SERVICE,
            ].includes(method)
          ) {
            productItem.push({
              vendorId: "",
              productType: "",
            });
          }
          setData((prevState) => ({
            ...prevState,
            setting: productItem,
          }));
        }

        break;
      default:
        break;
    }
  };
  const closeBanner = () => {
    setBanner({
      isOpen: false,
      status: "",
      title: "",
    });
  };

  if (addressLoading || vendorAndProductTypesLoading) {
    return (
      <BlockStack gap="8">
        <SkeletonBodyText />
      </BlockStack>
    );
  }

  return (
    <div>
      {banner.isOpen && (
        <Banner isOpen={banner.isOpen} status={banner.status} title={banner.title} onDismiss={() => closeBanner()} />
      )}
      <br />
      <BlockStack gap="200">
        <RadioButton
          label={
            (method === constant.HYBRID && cms("hybrid.dropShip.dropByVendor")) ||
            (method === constant.DEFAULT_CONSIGNMENT_SHIP && cms("consignmentShip.vendorType")) ||
            (method === constant.HYBRID_CONSIGNMENT_PICKUP && cms("hybrid.consignmentPick.consignmentByVendor")) ||
            (method === constant.HYBRID_CLICK_RESERVE && cms("hybrid.clickReserve.byVendor")) ||
            (method === constant.HYBRID_CLICK_COLLECT && cms("hybrid.clickCollect.byVendor")) ||
            (method === constant.HYBRID_TIME_SERVICE && cms("hybrid.timeService.byVendor"))
          }
          id="vendor"
          checked={shipData && shipData.by === constant.VENDOR}
          onChange={handleSelectChange}
        />

        {(type !== constant.HYBRID ||
          (type === constant.HYBRID && selectedTab === 1 && !dropship?.isThirdPartyShipping) ||
          (type === constant.HYBRID && selectedTab === 2 && !consignmentShip?.isThirdPartyShipping) ||
          (type === constant.HYBRID && selectedTab === 3 && consignmentPickup) ||
          (type === constant.HYBRID && selectedTab === 4 && clickCollect) ||
          (type === constant.HYBRID && selectedTab === 5 && clickReserve) ||
          (type === constant.HYBRID && selectedTab === 6 && timeService)) && (
          <>
            <RadioButton
              label={
                (method === constant.HYBRID && cms("hybrid.dropShip.dropByProductType")) ||
                (method === constant.DEFAULT_CONSIGNMENT_SHIP && cms("consignmentShip.productType")) ||
                (method === constant.HYBRID_CONSIGNMENT_PICKUP &&
                  cms("hybrid.consignmentPick.consignmentByProductType")) ||
                (method === constant.HYBRID_CLICK_RESERVE && cms("hybrid.clickReserve.byProduct")) ||
                (method === constant.HYBRID_CLICK_COLLECT && cms("hybrid.clickCollect.byProduct")) ||
                (method === constant.HYBRID_TIME_SERVICE && cms("hybrid.timeService.byProduct"))
              }
              id="productType"
              checked={shipData && shipData.by === constant.PRODUCT_TYPE}
              onChange={handleSelectChange}
            />
            <RadioButton
              label={
                (method === constant.HYBRID && cms("hybrid.dropShip.dropByVendorAndProductType")) ||
                (method === constant.DEFAULT_CONSIGNMENT_SHIP && cms("consignmentShip.bothType")) ||
                (method === constant.HYBRID_CONSIGNMENT_PICKUP &&
                  cms("hybrid.consignmentPick.consignmentByVendorAndProductType")) ||
                (method === constant.HYBRID_CLICK_RESERVE && cms("hybrid.clickReserve.byVendorAndProduct")) ||
                (method === constant.HYBRID_CLICK_COLLECT && cms("hybrid.clickCollect.byVendorAndProduct")) ||
                (method === constant.HYBRID_TIME_SERVICE && cms("hybrid.timeService.byVendorAndProduct"))
              }
              checked={shipData && shipData.by === constant.VENDOR_PRODUCT_TYPE}
              id="vendorAndProductType"
              onChange={handleSelectChange}
            />
          </>
        )}
      </BlockStack>
      <br />
      {shipData &&
        shipData.by === constant.VENDOR &&
        shipData.setting &&
        shipData.setting.map((value, index) => {
          return consignmentByVendor(value, index);
        })}
      <BlockStack gap="400">
        {shipData &&
          shipData.by === constant.PRODUCT_TYPE &&
          shipData.setting &&
          shipData.setting.map((value, index) => {
            return consignmentByProductType(value, index);
          })}
      </BlockStack>
      <BlockStack gap="400">
        {shipData &&
          shipData.by === constant.VENDOR_PRODUCT_TYPE &&
          shipData.setting &&
          shipData.setting.map((value, index) => {
            return consignmentByBoth(value, index);
          })}
      </BlockStack>
      {((shipData && shipData.by === constant.VENDOR) ||
        (shipData && shipData.by === constant.PRODUCT_TYPE) ||
        (shipData && shipData.by === constant.VENDOR_PRODUCT_TYPE)) && (
        <div>
          <br />
          <Link id="addLink" onClick={() => addItem()}>
            {`+ ${cms("button.addMore")}`}
          </Link>
        </div>
      )}
    </div>
  );
};

ConsignmentShip.propTypes = {
  method: PropTypes.string.isRequired,
};

export default withErrorBoundary(ConsignmentShip);
